import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { IDropdownOption } from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { ILocationItem } from '../../../../../components/custom/LocationTree';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import * as enums from '../../../../../enums';
import { locationType } from '../../../../../enums';

interface ILocationTreeNodeCreateWindowProps {
    organizationInformationId: number;
    refetchLocations(organizationId: number): Promise<void>;
    selectedLocation: ILocationItem;
    openLocationTreeNodeCreateWindow: (selectedLocation: ILocationItem, basedOnInitialValues: ILocationItem | null) => Promise<void>;
    basedOnInitialValues: ILocationItem | null;
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Šis laukas yra privalomas'),
    locationType: Yup.number().required('Šis laukas yra privalomas')
});

const LocationTreeNodeCreateWindow: FC<IWindowComponentProps<ILocationTreeNodeCreateWindowProps>> = (props) => {
    const {
        componentProps: { refetchLocations, openLocationTreeNodeCreateWindow, selectedLocation, organizationInformationId, basedOnInitialValues },
        onClose
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [createNext, setCreateNext] = useState<boolean>(false);
    const [levelOptions, setLevelOptions] = useState<number[] | null>(null);

    const mapFloorsToOptions = (floors: number[]): IDropdownOption[] => {
        var floorOptions: IDropdownOption[] = [];

        floors.map((x) => {
            floorOptions.push({
                key: x,
                text: x.toString()
            });
        });
        return floorOptions;
    };

    const returnAvailableTypes = (selectedLocation: ILocationItem): fabric.IDropdownOption[] => {
        var availableTypes: fabric.IDropdownOption[] = [];
        enums.locationType.displayTypesArray().map((x) => {
            if (x.key > selectedLocation.locationType) {
                if (!(selectedLocation.locationType === locationType.Building && (x.key === locationType.Cabinet || x.key === locationType.Room))) {
                    availableTypes.push(x);
                }
            }
        });
        return availableTypes;
    };

    const handleSubmit = async (values: ILocationItem) => {
        try {
            setIsLoading(true);
            const request = { ...values, partOfLocationId: selectedLocation.id };
            if (request.locationType !== locationType.Level) {
                request.floor = null;
            }
            await api.administration.createLocation(request);
            toast.success('Lokacija sėkmingai sukurta');
            refetchLocations(organizationInformationId);
            onClose();
            if (createNext) {
                await openLocationTreeNodeCreateWindow(selectedLocation, values);
            }
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
            fetchData();
        }
    };

    const fetchData = async () => {
        if (selectedLocation) {
            const options = await api.administration.getAvailableFloors(selectedLocation.id);
            setLevelOptions(options);
        }
    };
    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {levelOptions && (
                        <formik.Formik
                            validationSchema={validationSchema}
                            initialValues={
                                basedOnInitialValues === null
                                    ? {
                                          id: 0,
                                          partOfLocationId: selectedLocation!.id,
                                          name: '',
                                          locationStatus: enums.locationStatus.Closed,
                                          locationType:
                                              selectedLocation!.locationType === locationType.Building
                                                  ? enums.locationType.Level
                                                  : enums.locationType.Cabinet,
                                          key: null,
                                          doorControllerKey: null,
                                          activeTicketPrintingTimeBeforeVisit: null,
                                          activeTicketPrintingTimeAfterVisit: null,
                                          floor: levelOptions.length > 0 ? levelOptions[0] : null,
                                          isActiveTicketPrintingTimeAfterVisitEnabled: null,
                                          isActiveTicketPrintingTimeBeforeVisitEnabled: null
                                      }
                                    : { ...basedOnInitialValues, name: '' }
                            }
                            onSubmit={handleSubmit}
                        >
                            {(formikProps) => (
                                <formik.Form>
                                    <div className="ms-Grid">
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                <formik.Field
                                                    name="name"
                                                    render={({ field, form }: formik.FieldProps<string>) => (
                                                        <fabric.TextField
                                                            label="Pavadinimas"
                                                            placeholder="Įrašykite objekto pavadinimą"
                                                            {...field}
                                                            errorMessage={
                                                                (formikProps.status && formikProps.status.name) ||
                                                                (form.errors.name && form.errors.name)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                <fabric.TextField
                                                    disabled
                                                    value={selectedLocation && selectedLocation.name ? selectedLocation.name.toString() : ''}
                                                    label="Tėvinė lokacija"
                                                />
                                            </div>
                                        </div>
                                        <div className="ms-Grid-row">
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                <formik.Field
                                                    name="locationType"
                                                    render={({ field, form }: formik.FieldProps<enums.LocationType>) => (
                                                        <fabric.Dropdown
                                                            label="Lokacijos tipas"
                                                            selectedKey={field.value}
                                                            options={selectedLocation ? returnAvailableTypes(selectedLocation) : []}
                                                            defaultSelectedKey={field.value}
                                                            onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                            errorMessage={
                                                                (formikProps.status && formikProps.status.locationType) ||
                                                                (form.errors.locationType && form.errors.locationType)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                {formikProps.values.locationType === locationType.Level && levelOptions && (
                                                    <formik.Field
                                                        name="floor"
                                                        render={({ field, form }: formik.FieldProps<number>) => (
                                                            <fabric.Dropdown
                                                                disabled={formikProps.values.locationType !== locationType.Level}
                                                                label="Aukštas"
                                                                options={mapFloorsToOptions(levelOptions)}
                                                                placeholder="Pastate nėra laisvų aukštų"
                                                                selectedKey={field.value}
                                                                onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                            <fabric.Checkbox
                                                label="Kurti kitą"
                                                checked={createNext}
                                                onChange={() => setCreateNext(!createNext)}
                                            ></fabric.Checkbox>
                                            <fabric.PrimaryButton
                                                className="ms-slideRightIn40 ms-textAlignRight"
                                                text="Išsaugoti"
                                                disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                                                type="submit"
                                            />
                                        </div>
                                    </div>
                                </formik.Form>
                            )}
                        </formik.Formik>
                    )}
                </>
            )}
        </>
    );
};

export default LocationTreeNodeCreateWindow;
